import { currentUserQueryKey } from '@entities/users/apis/user-query-key'
import { getQueryClient } from '@shared/apis/tanstack-query/query-client'
import { getMetaContextHeaders } from '@shared/marketing/meta-context'

/** Read only the profile belonging to this request. Hydration needs no extra fetch or global country. */
export function getProfileMetaContextHeaders(bearerToken?: string): Record<string, string> {
  if (typeof window === 'undefined' || !bearerToken) return {}
  const profile = getQueryClient().getQueryData<{ countryCode?: string | null }>(currentUserQueryKey(bearerToken))
  return getMetaContextHeaders(profile?.countryCode ?? null)
}

export const META_PROFILE_WAIT_BUDGET_MS = 800

/** Reuse an already-running authenticated lookup; never start/cancel it or hold business work indefinitely. */
export async function prepareProfileMetaContextHeaders(bearerToken?: string): Promise<Record<string, string>> {
  try {
    if (
      typeof window === 'undefined' ||
      !bearerToken ||
      process.env.NEXT_PUBLIC_META_CAPI_CAPTURE_ENABLED !== 'true' ||
      process.env.NEXT_PUBLIC_META_CAPI_PRIVACY_APPROVED !== 'true'
    )
      return {}
    if (
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl ||
      navigator.doNotTrack === '1' ||
      navigator.doNotTrack === 'yes'
    )
      return getMetaContextHeaders(null) // Reuse refusal cleanup without waiting for a profile.
    if (window.ReactNativeWebView || /PodoApp\/RN/i.test(navigator.userAgent)) return {}
    const query = getQueryClient()
      .getQueryCache()
      .find({ queryKey: currentUserQueryKey(bearerToken), exact: true })
    if (query?.state.data === undefined && query?.state.fetchStatus === 'fetching' && query.promise) {
      let timeout: ReturnType<typeof setTimeout> | undefined
      try {
        await Promise.race([
          query.promise.catch(() => undefined),
          new Promise<void>((resolve) => {
            timeout = setTimeout(resolve, META_PROFILE_WAIT_BUDGET_MS)
          }),
        ])
      } finally {
        clearTimeout(timeout)
      }
    }
    return getProfileMetaContextHeaders(bearerToken)
  } catch {
    // Optional attribution: unavailable/slow country remains unknown; original business work proceeds.
    return {}
  }
}
